/**
 * ActionBar Component
 *
 * Action bar with Stop / Submit / Submit-and-Stop buttons for the moderation
 * work queue. Displays pending case count and applied-label count.
 *
 * @see T038 Submit (N), T039 Submit and Stop, T049 Stop Moderation
 */

import type { PreviewOptions } from '@moderation/domain/PreviewOptions';
import { Button, ButtonType, LoadingSpinner, Switch, Text } from '@soteria/design-system-ve';
import type { ReactElement } from 'react';

import * as styles from '../ModerationWorkQueuePage.css';

import { ModerationCountdownTimer } from './ModerationCountdownTimer';

export interface ActionBarProps {
  /** Whether batch submission is in progress */
  submitLoading: boolean;
  /** Stop session and return to dashboard */
  handleStopModeration: () => void;
  /** Submit applied cases and load next batch */
  handleSubmit: () => Promise<void>;
  /** Submit applied cases and return to dashboard */
  handleSubmitAndStop: () => Promise<void>;
  /** Number of cases on the current page still awaiting labels */
  pendingCount: number;
  /** Number of cases with labels applied */
  appliedCount: number;
  /** Whether Submit button is disabled */
  isSubmitDisabled: boolean;
  /** Current preview effect toggle states */
  previewOptions: PreviewOptions;
  /** Callback when a preview toggle is changed */
  onTogglePreview: (key: keyof PreviewOptions) => void;
  /** Formatted countdown time remaining (e.g. "4:32") */
  formattedTime?: string;
  /** Whether the session is close to expiring (≤ 60 s) */
  isExpiringSoon?: boolean;
}

/**
 * Renders the moderation work queue action bar with Stop, Submit (N), and
 * Submit and Stop buttons.
 */
export function ActionBar({
  submitLoading,
  handleStopModeration,
  handleSubmit,
  handleSubmitAndStop,
  pendingCount,
  appliedCount,
  isSubmitDisabled,
  previewOptions,
  onTogglePreview,
  formattedTime,
  isExpiringSoon,
}: ActionBarProps): ReactElement {
  return (
    <div aria-busy={submitLoading} className={styles.actionBar}>
      <div className={styles.leftGroup}>
        <Text aria-live="polite">
          {pendingCount} pending {pendingCount === 1 ? 'case' : 'cases'}
        </Text>

        <div aria-label="Preview controls" className={styles.previewsGroup} role="group">
          <Text>Previews:</Text>
          <Switch checked={previewOptions.blur} label="Blur" size="small" onChange={() => onTogglePreview('blur')} />
          <Text>Blur</Text>
          <Switch checked={previewOptions.gray} label="Gray" size="small" onChange={() => onTogglePreview('gray')} />
          <Text>Gray</Text>
          <Switch checked={previewOptions.flip} label="Flip" size="small" onChange={() => onTogglePreview('flip')} />
          <Text>Flip</Text>
        </div>
      </div>

      <div className={styles.actionButtonGroup}>
        {formattedTime && <ModerationCountdownTimer formattedTime={formattedTime} isExpiringSoon={isExpiringSoon ?? false} />}
        <Button aria-label="Stop moderation and return to dashboard" buttonType={ButtonType.Danger} disabled={submitLoading} onClick={handleStopModeration}>
          Stop Moderation
        </Button>
        <Button
          aria-disabled={isSubmitDisabled}
          aria-label={`Submit ${appliedCount} ${appliedCount === 1 ? 'case' : 'cases'} and load next batch`}
          buttonType={ButtonType.Primary}
          disabled={isSubmitDisabled}
          title={isSubmitDisabled && !submitLoading ? 'All cases must have labels to submit' : undefined}
          onClick={handleSubmit}
        >
          {submitLoading ? <LoadingSpinner /> : `Submit (${appliedCount})`}
        </Button>
        <Button
          aria-label="Submit labeled cases and return to dashboard"
          buttonType={ButtonType.Secondary}
          disabled={isSubmitDisabled}
          title={isSubmitDisabled && !submitLoading ? 'All cases must have labels to submit' : undefined}
          onClick={handleSubmitAndStop}
        >
          Submit and Stop
        </Button>
      </div>
    </div>
  );
}
